import React, { useEffect, useState, useCallback } from "react";
import { Col, Row, Select, Tag, message, Button, Modal, Input, Dropdown, Drawer } from "antd";
import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import { MoreOutlined, SearchOutlined } from "@ant-design/icons";
import { Terminal, Copy } from "lucide-react";
import axios from "axios";

// Import your constants or environment variables
import { AUTH_TOKEN, REACT_APP_API, ORG_ID, DOMAIN_ID } from "../../../constants";
import { formatTimeByTimezone } from "../../../common/formatTimebyTimezone";
import MySqlTerminal from "./MySqlTerminal";

// Custom "No Rows" overlay for the DataGrid
const CustomNoRowsOverlay = () => (
  <GridOverlay>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        color: "#666",
      }}
    >
      <span>No Data</span>
    </div>
  </GridOverlay>
);

const PAGE_SIZE = 8;

const ConnectionsScreen = ({ showNotification }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);
  const [selectedPrivilege, setSelectedPrivilege] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  // Dropdown filter lists
  const [databaseList, setDatabaseList] = useState([]);
  const [userList, setUserList] = useState([]);

  // Actual connections displayed in the DataGrid
  const [connectionData, setConnectionData] = useState([]);

  // Single source of truth for pagination (DataGrid -> server pagination)
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // 0-based index
    pageSize: PAGE_SIZE,
  });

  // For user notifications
  const [notification, contextHolder] = message.useMessage();

  // For connection string modal
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);

  // For terminal drawer
  const [isTerminalDrawerOpen, setIsTerminalDrawerOpen] = useState(false);
  const [selectedTerminalConnection, setSelectedTerminalConnection] = useState(null);

  // ------------------------------------------------------------
  // 1. On mount, fetch lists for filters (databases/users)
  // ------------------------------------------------------------
  useEffect(() => {
    fetchFilterLists();
  }, []);

  // ------------------------------------------------------------
  // 2. Whenever filters/pagination changes -> fetch data
  // ------------------------------------------------------------
  useEffect(() => {
    fetchConnectionData();
  }, [
    selectedDatabase,
    selectedUser,
    selectedHost,
    selectedPrivilege,
    paginationModel,
    searchValue,
  ]);

  // ------------------------------------------------------------
  // Helper: Fetch unique DB names & user names
  // ------------------------------------------------------------
  const fetchFilterLists = async () => {
    try {
      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
      };

      // 1) Unique database names
      const dbRes = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getUniqueDatabasenames`,
        payload,
        { headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true } }
      );
      // 2) Unique user names
      const userRes = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/getUniqueUsernames`,
        payload,
        { headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true } }
      );

      setDatabaseList(dbRes.data?.db_names || []);
      setUserList(userRes.data?.usernames || []);
    } catch (error) {
      console.error("Error fetching filter lists:", error);
      notification.error({ content: "Unable to fetch database/user lists" });
    }
  };

  // ------------------------------------------------------------
  // Helper: Fetch connections with the applied filters
  // ------------------------------------------------------------
  const fetchConnectionData = () => {
    const filters = [];
    if (selectedHost) {
      filters.push({ filterParameter: "hostname", filterValue: selectedHost });
    }
    if (selectedPrivilege) {
      filters.push({ filterParameter: "privilege", filterValue: selectedPrivilege });
    }
    if (selectedUser) {
      filters.push({ filterParameter: "user", filterValue: selectedUser });
    }
    if (selectedDatabase) {
      filters.push({ filterParameter: "database_name", filterValue: selectedDatabase });
    }
    if (searchValue) {
      filters.push({ filterParameter: "connection_string", filterValue: searchValue });
    }

    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      filters,
      requestId: "",
      page_id: paginationModel.page + 1, // server is 1-based
      limit: paginationModel.pageSize,
    };

    setIsLoading(true);

    let userId = localStorage.getItem("userId");
    let email = localStorage.getItem("UserName");

    axios
      .post(`${REACT_APP_API}/api/v1/databaseService/listConnections`, payload, {
        headers: {
          "X-Authorization": AUTH_TOKEN(),
          userid: userId,
          email: email,
          withCredentials: true,
        },
      })
      .then((res) => {
        setIsLoading(false);

        const dataArray = res?.data?.data || [];
        // We must give each row a unique "id" for MUI
        const mapped = dataArray.map((item, index) => ({
          ...item,
          id: item.id + "-" + index, // or item.id
        }));

        setConnectionData(mapped);
        setTotalCount(res?.data?.total_count || 0);
      })
      .catch((err) => {
        setIsLoading(false);
        console.error("Error fetching connections:", err);
        notification.error({ content: "Unable to fetch connections" });
      });
  };

  // ------------------------------------------------------------
  // Reset pagination whenever a filter changes
  // ------------------------------------------------------------
  const resetPageAndSetState = (setter) => (value) => {
    setter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Search both host and connection string
  const handleSearch = (value) => {
    setSearchValue(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // ------------------------------------------------------------
  // Parse the connection string to extract host, port, user, db
  // ------------------------------------------------------------
  const parseConnectionString = (connStr) => {
    // Initialize with empty values
    let user = "";
    let host = "";
    let port = "";
    let db = "";

    // Extract username (format: -uuser or -u user)
    const userRegex = /-u([^ ,]+)/;
    const userMatch = connStr.match(userRegex);
    if (userMatch) {
      user = userMatch[1].split(",")[0]; // Handle case where ID follows username with comma
    }

    // Extract host (format: -hhost or -h host)
    const hostRegex = /-h([^ ]+)/;
    const hostMatch = connStr.match(hostRegex);
    if (hostMatch) {
      host = hostMatch[1];
    }

    // Extract port (format: -Pport or -P port)
    const portRegex = /-P([^ ]+)/;
    const portMatch = connStr.match(portRegex);
    if (portMatch) {
      port = portMatch[1];
    }

    // Extract database (format: -Ddb or -D db)
    const dbRegex = /-D([^ ]+)/;
    const dbMatch = connStr.match(dbRegex);
    if (dbMatch) {
      db = dbMatch[1];
    }

    return { user, host, port, db, rawConnString: connStr };
  };

  // ------------------------------------------------------------
  // Menu items for each row: connect or copy
  // ------------------------------------------------------------
  const getMenuItems = (connection) => [
    {
      key: "connect",
      label: "Use Web Console",
      icon: <Terminal size={16} />,
    },
    {
      key: "copy",
      label: "Use Direct Access",
      icon: <Copy size={16} />,
    },
  ];

  // ------------------------------------------------------------
  // Handle each menu action
  // ------------------------------------------------------------
  const handleMenuClick = (connection, actionKey) => {
    if (actionKey === "connect") {
      setSelectedTerminalConnection(connection);
      setIsTerminalDrawerOpen(true);
    } else if (actionKey === "copy") {
      setSelectedConnection(connection);
      setIsConnectionModalOpen(true);
    }
  };

  // ------------------------------------------------------------
  // Copy the entire connection string from the modal
  // ------------------------------------------------------------
  const handleCopyConnectionString = () => {
    if (selectedConnection) {
      navigator.clipboard.writeText(selectedConnection.connection_string);
      notification.success({ content: "Connection string copied!" });
      setIsConnectionModalOpen(false);
    }
  };

  // ------------------------------------------------------------
  // Terminal drawer close handlers
  // ------------------------------------------------------------
  // Use useCallback to memoize the function reference, preventing unnecessary re-renders of MySqlTerminal
  const handleTerminalExit = useCallback(() => {
    console.log("Terminal exit triggered from child component.");
    setIsTerminalDrawerOpen(false);
    setSelectedTerminalConnection(null); // Clear the selected connection
    notification.info({ content: "Terminal session closed." }); // Optional notification
  }, [notification]); // Dependencies for useCallback

  // Define the function for manual drawer close
  const handleDrawerClose = useCallback(() => {
    setIsTerminalDrawerOpen(false);
    setSelectedTerminalConnection(null);
    // Optionally, add logic here if you need to differentiate manual close vs exit command close
  }, []);

  // ------------------------------------------------------------
  // DataGrid columns
  // ------------------------------------------------------------
  const columns = [
    {
      headerName: "Hostname",
      field: "ip_address",
      flex: 1,
    },
    {
      headerName: "Database",
      field: "database_name",
      flex: 1.5,
    },
    {
      headerName: "Users",
      field: "users",
      flex: 1.5,
      renderCell: (params) =>
        Array.isArray(params.value) ? (
          <>
            {params.value.map((user) => (
              <Tag key={user}>{user}</Tag>
            ))}
          </>
        ) : null,
    },
    {
      headerName: "Privileges",
      field: "user_privilege",
      flex: 1.5,
      renderCell: (params) =>
        Array.isArray(params.value) ? (
          <>
            {params.value.map((priv) => {
              let color = "blue";
              if (priv === "READ") color = "green";
              else if (priv === "WRITE") color = "orange";
              else if (priv === "EXECUTE") color = "purple";
              return (
                <Tag key={priv} color={color}>
                  {priv}
                </Tag>
              );
            })}
          </>
        ) : null,
    },
    {
      headerName: "Actions",
      field: "actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Dropdown
          menu={{
            items: getMenuItems(params.row),
            onClick: ({ key }) => handleMenuClick(params.row, key),
          }}
          placement="bottomRight"
          trigger={["click"]}
        >
          <Button type="primary">Connect</Button>
        </Dropdown>
      ),
    },
  ];

  // ------------------------------------------------------------
  // For the filter tags
  // ------------------------------------------------------------
  const appliedFilters = [];
  if (selectedDatabase) {
    appliedFilters.push({ key: "Database", display: `Database: ${selectedDatabase}` });
  }
  if (selectedUser) {
    appliedFilters.push({ key: "User", display: `User: ${selectedUser}` });
  }
  if (selectedHost) {
    appliedFilters.push({ key: "Host", display: `Host: ${selectedHost}` });
  }
  if (selectedPrivilege) {
    appliedFilters.push({ key: "Privilege", display: `Privilege: ${selectedPrivilege}` });
  }
  if (searchValue) {
    appliedFilters.push({ key: "Search", display: `Search: ${searchValue}` });
  }

  return (
    <>
      {contextHolder}
      <div className="content-container">
        {/* Title / Description */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <h2 className="title">Database Connections</h2>
            <p className="subtitle">Manage and monitor your database connections</p>
          </Col>
        </Row>

        {/* Filter Section */}
        <div className="filter-section">
          <Row gutter={[16, 16]}>
            {/* Database Filter */}
            <Col xs={24} sm={12} md={6} lg={4}>
              <div className="filter-item">
                <label className="filter-label">Database</label>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Database"
                  style={{ width: "100%" }}
                  value={selectedDatabase}
                  onChange={resetPageAndSetState(setSelectedDatabase)}
                  filterOption={(input, option) =>
                    (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {databaseList.map((db) => (
                    <Select.Option key={db} value={db}>
                      {db}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>

            {/* User Filter */}
            <Col xs={24} sm={12} md={6} lg={4}>
              <div className="filter-item">
                <label className="filter-label">User</label>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select User"
                  style={{ width: "100%" }}
                  value={selectedUser}
                  onChange={resetPageAndSetState(setSelectedUser)}
                  filterOption={(input, option) =>
                    (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {userList.map((user) => (
                    <Select.Option key={user} value={user}>
                      {user}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </Col>

            {/* Host Filter */}
            <Col xs={24} sm={12} md={6} lg={4}>
              <div className="filter-item">
                <label className="filter-label">Hostname</label>
                <Input
                  allowClear
                  placeholder="Enter Hostname"
                  value={selectedHost}
                  onChange={(e) => resetPageAndSetState(setSelectedHost)(e.target.value)}
                />
              </div>
            </Col>

            {/* Privilege Filter */}
            <Col xs={24} sm={12} md={6} lg={4}>
              <div className="filter-item">
                <label className="filter-label">Privilege</label>
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Privilege"
                  style={{ width: "100%" }}
                  value={selectedPrivilege}
                  onChange={resetPageAndSetState(setSelectedPrivilege)}
                  filterOption={(input, option) =>
                    (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                >
                  <Select.Option value="READ">READ</Select.Option>
                  <Select.Option value="WRITE">WRITE</Select.Option>
                  <Select.Option value="EXECUTE">EXECUTE</Select.Option>
                </Select>
              </div>
            </Col>

            {/* Search Button */}
            <Col xs={24} sm={12} md={6} lg={4}>
              <div style={{ display: "flex", alignItems: "flex-end", height: "85%" }}>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={fetchConnectionData}
                  style={{ width: "100%" }}
                >
                  Search
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {/* Applied Filter Tags */}
        {appliedFilters.length > 0 && (
          <div className="applied-filters">
            <p className="filter-tags-label">Applied Filters</p>
            <div className="filter-tags">
              {appliedFilters.map(({ key, display }) => (
                <Tag
                  key={key}
                  closable
                  onClose={() => {
                    if (key === "Database") {
                      resetPageAndSetState(setSelectedDatabase)(null);
                    } else if (key === "User") {
                      resetPageAndSetState(setSelectedUser)(null);
                    } else if (key === "Hostname") {
                      resetPageAndSetState(setSelectedHost)(null);
                    } else if (key === "Privilege") {
                      resetPageAndSetState(setSelectedPrivilege)(null);
                    } else if (key === "Search") {
                      handleSearch("");
                    }
                  }}
                  className="filter-tag"
                >
                  {display}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {/* DataGrid with server pagination */}
        <div className="data-grid-container">
          <DataGrid
            components={{
              NoRowsOverlay: CustomNoRowsOverlay,
            }}
            rows={connectionData}
            columns={columns}
            paginationMode="server"
            rowCount={totalCount}
            loading={isLoading}
            className="custom-data-grid"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel} // Simplified handler
          />
        </div>
      </div>

      {/* Connection String Modal */}
      <Modal
        title="Connection Details"
        open={isConnectionModalOpen}
        onCancel={() => setIsConnectionModalOpen(false)}
        centered
        width={1200}
        className="connection-modal"
        footer={[
          <Button
            key="copy"
            icon={<Copy size={16} />}
            type="primary"
            onClick={handleCopyConnectionString}
            className="copy-button"
          >
            Copy
          </Button>,
          <Button
            key="close"
            onClick={() => setIsConnectionModalOpen(false)}
            className="close-button"
          >
            Close
          </Button>,
        ]}
      >
        {selectedConnection && (
          <div className="connection-details">
            <h3>Instructions</h3>
            <p>
              Please copy and paste the following command to establish a connection to the database.
            </p>
            <pre
              className="connection-string"
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
            >
              {selectedConnection.connection_string}
            </pre>
          </div>
        )}
      </Modal>

      {/* Terminal Drawer */}
      <Drawer
        title="Web Console"
        placement="bottom"
        onClose={handleDrawerClose}
        open={isTerminalDrawerOpen}
        height="80%"
        destroyOnClose={true}
        className="terminal-drawer"
      >
        {selectedTerminalConnection && isTerminalDrawerOpen && (
          <MySqlTerminal
            connectionString={selectedTerminalConnection.connection_string}
            onTerminalExit={handleTerminalExit}
          />
        )}
      </Drawer>

      {/* Inline Styles (JSX) */}
      <style jsx>{`
        .content-container {
          padding: 24px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .title {
          font-size: 24px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0;
        }

        .subtitle {
          font-size: 14px;
          color: #666;
          margin: 4px 0 0;
        }

        .filter-section {
          background: #f8f9fa;
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 24px;
        }

        .filter-item {
          margin-bottom: 8px;
        }

        .filter-label {
          display: block;
          margin-bottom: 8px;
          color: #666;
          font-size: 14px;
        }

        .applied-filters {
          margin-bottom: 16px;
        }

        .filter-tags-label {
          color: #666;
          margin-bottom: 8px;
        }

        .filter-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .filter-tag {
          background: #e6f7ff;
          border-color: #91d5ff;
          color: #1890ff;
        }

        .data-grid-container {
          background: #fff;
          border-radius: 8px;
          overflow: hidden;
        }

        .custom-data-grid {
          border: none !important;
        }

        .connection-modal :global(.ant-modal-content) {
          border-radius: 8px;
        }

        .connection-details {
          padding: 24px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .connection-details h3 {
          margin-bottom: 16px;
          color: #1a1a1a;
        }

        .connection-details p {
          color: #666;
          margin-bottom: 16px;
        }

        .connection-string {
          background: #fff;
          padding: 16px;
          border-radius: 4px;
          border: 1px solid #e8e8e8;
          overflow-x: auto;
          font-family: monospace;
          font-size: 14px;
          line-height: 1.5;
        }

        .copy-button {
          background: #1890ff;
          border-color: #1890ff;
        }

        .close-button {
          margin-left: 8px;
        }

        .terminal-drawer :global(.ant-drawer-content-wrapper) {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }

        .terminal-drawer :global(.ant-drawer-body) {
          padding: 0;
          height: 100%;
        }
      `}</style>
    </>
  );
};

export default ConnectionsScreen;
